// ============================================================================
// programColors.js — canonical program (species) accent palette · CP0 §A12
// ----------------------------------------------------------------------------
// ONE source of truth for program identity color. Ratified 2026-06-16; these
// SUPERSEDE both prior systems — the Home island `--c-*` OKLCH dots and the
// legacy inline `kindColors`/`kindBg` pairs. As surfaces are touched (CP1→CP5)
// they migrate onto this module; do not reintroduce the old values.
//
// Treatment rules (A12):
//   • Program color appears ONLY as a solid dot or a solid SELECTED pill
//     (nav / tabs), and optionally one headline figure. Never a full-page
//     theme, card background, body text, or a status color.
//   • Program ≠ semantic by TREATMENT, not just hue: program = solid dot/pill;
//     semantic status = soft-bg badge or short ink text. That separation keeps
//     pig-blue from reading as `info` and cattle-brick from reading as `danger`.
//   • Selected pill text color comes from getReadableText() so it auto-picks
//     black on the light hues (broiler/layer/sheep) and white on pig/cattle/
//     gray — no contrast failures.
//
// Hues come from the owner's handoff 2026-10 package (docs/design/
// handoff-2026-10), which supersedes the 2026-06-16 tuning. Two collisions the
// old hues dodged are back, and TREATMENT is what keeps them apart:
//   • cattle #C0452F is the same red as the `--danger` token. Program color is
//     only ever a solid dot or a selected pill; danger is a soft-tinted badge
//     or ink text, never a dot.
//   • sheep #7FB83A sits near the brand green and the sea-green `ok`.
//   • broiler yellow vs layer orange still read apart at 9–12px dot size.
// ============================================================================
import type {CSSProperties} from 'react';

import {getReadableText} from './styles.js';

// Canonical hexes. Keyed by the stable program/species string used across the
// app (matches the daily `kind` strings: broiler/layer/pig/cattle/sheep).
export const PROGRAM_COLORS = {
  pig: '#4A55A2', // indigo/navy — deeper than the azure `info` semantic
  broiler: '#D08A2B', // amber — clearly yellower than layer
  layer: '#D06A2B', // burnt orange — redder than broiler
  cattle: '#C0452F', // brick red — the same hex as `--danger`; see the note above
  sheep: '#7FB83A', // grass green — lighter than the brand green and `ok`
  equipment: '#6B7280', // slate gray
};

// The canonical program keys, derived from the palette rather than re-listed —
// a seventh program can only be added by adding its hex above.
export type ProgramKey = keyof typeof PROGRAM_COLORS;

// Aliases for call sites that use a different label for the same accent.
const PROGRAM_ALIASES = {
  broilers: 'broiler',
  layers: 'layer',
  pigs: 'pig',
  sheeps: 'sheep',
  admin: 'equipment',
  equip: 'equipment',
  egg: 'layer', // eggs ride the layer accent
};

// Neutral fallback for an unknown program — the slate gray, never a guess.
export const PROGRAM_FALLBACK = PROGRAM_COLORS.equipment;

// Both tables are looked up by a lowercased runtime string that is usually NOT
// a known program key — the whole point of the fallback. These aliases describe
// that access without loosening either table's own literal type, so the six
// ratified hexes stay pinned above and `PROGRAM_COLORS.sheep` still resolves
// statically for anything that reaches for a specific program.
const COLOR_BY_KEY: Readonly<Record<string, string | undefined>> = PROGRAM_COLORS;
const ALIAS_BY_KEY: Readonly<Record<string, string | undefined>> = PROGRAM_ALIASES;

/** Resolve a program/species key (or alias) to its canonical accent hex. */
export function getProgramColor(key: string | null | undefined): string {
  if (!key) return PROGRAM_FALLBACK;
  const k = String(key).toLowerCase();
  const direct = COLOR_BY_KEY[k];
  if (direct) return direct;
  const aliased = ALIAS_BY_KEY[k];
  return (aliased && COLOR_BY_KEY[aliased]) || PROGRAM_FALLBACK;
}

/**
 * Inline style for a solid program dot (the in-list species marker).
 * Default 7px — the constitution's species dot (§3, C05); pass `size` to scale.
 */
export function programDotStyle(key: string | null | undefined, size = 7): CSSProperties {
  return {
    display: 'inline-block',
    width: size,
    height: size,
    flex: `0 0 ${size}px`,
    borderRadius: '50%',
    background: getProgramColor(key),
  };
}

/**
 * Inline style for a tab/nav pill. Unselected = transparent, no border (A12 /
 * Tab decision). Selected = solid program fill with auto-contrast text.
 * `radius` defaults to 999 — a selected nav/tab pill is fully rounded (CP0 Tabs decision),
 * not a floor-radius control; pass `radius` to override.
 */
export function programPillStyle(
  key: string | null | undefined,
  selected: boolean,
  {radius = 999}: {radius?: CSSProperties['borderRadius']} = {},
): CSSProperties {
  const fill = getProgramColor(key);
  return {
    padding: '7px 16px',
    borderRadius: radius,
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 12,
    fontWeight: selected ? 700 : 500,
    whiteSpace: 'nowrap',
    background: selected ? fill : 'transparent',
    color: selected ? getReadableText(fill) : 'var(--text-secondary)',
  };
}
